import { FC, useContext, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../context/notificationContext";
import { TabsProvider } from "../../context/tabsContext";
import { useUserSettings } from "../../context/userSettingsContext";
import useDataInitializer from "../../hooks/useDataInitializer";
import { useI18n } from "../../hooks/useTranslations";
import { useRolesApi } from "../../http/roles/api";
import { UpsertRole } from "../../http/roles/types";
import Header from "../ui/Header";
import Panel from "../ui/Panel";
import Tabs, { TabContent, TabsContents } from "../ui/Tabs";
import RoleForm, { RoleFormBody } from "./RoleForm";
import UserRoles from "./userRoles/UserRoles";

const EditRole: FC = () => {
  const i18n = useI18n();

  return (
    <>
      <Header title={i18n.t.roles.edit} />
      <TabsProvider>
        <TabbedEditRole />
      </TabsProvider>
    </>
  );
};

export default EditRole;

const TabbedEditRole: FC = () => {
  const i18n = useI18n();
  const userSettings = useUserSettings();
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const rolesApi = useRolesApi();
  const aborter = useRef(new AbortController());

  const { id } = useParams<{ id: string }>();

  const roleSwr = rolesApi.getAdminRole(id!, aborter.current.signal);
  const [role, isInitialized] = useDataInitializer(roleSwr.data, roleSwr.error);

  useEffect(() => {
    id && roleSwr.trigger();
  }, [id]);

  useEffect(() => {
    roleSwr.error && dispatch(showErrorNotification(i18n, roleSwr.error));
  }, [roleSwr.error]);

  const onSubmit = async (formData: RoleFormBody): Promise<void> => {
    if (role?.id) {
      const body: UpsertRole = {
        name: formData.name,
        permissions: formData.permissions,
        departments: formData.departments,
      };

      setIsSubmitting(true);
      try {
        await rolesApi.updateAdminRole(role.id, body);
        dispatch(showSuccessNotification(i18n.t));
      } catch (error) {
        dispatch(showErrorNotification(i18n, error));
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const roleForm = (
    <RoleForm
      onSubmit={onSubmit}
      isDisabled={!userSettings.permissions.roles.update || isSubmitting || !role?.id}
      isLoading={!isInitialized}
      role={role}
    />
  );

  if (!userSettings.permissions.users.read) {
    return <Panel>{roleForm}</Panel>;
  }

  const tabs = [{ title: i18n.t.roles.edit }, { title: i18n.t.users.plural }];
  const tabComponents: TabContent[] = [
    { body: roleForm, eager: true, withBorder: true },
    { body: <UserRoles />, withBorder: true },
  ];

  return (
    <>
      <Tabs tabs={tabs} />
      <TabsContents contents={tabComponents} />
    </>
  );
};
